import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, Router, Scroll } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SettingsService } from './common/settings.service';
import { AnalyticsService } from './common/analytics.service';
import { LocationStrategy, ViewportScroller } from '@angular/common';
import { CookiePopupComponent } from './shared/cookies/cookie-popup.component';
import { filter, pairwise } from 'rxjs/operators';
import { Event } from '@angular/router';
import { PreserveQueryParamsStrategy } from './common/preserve-query-params-strategy';
import { WarningComponent } from './warning/warning.component';
import { NotFoundComponent } from './not-found/not-found.component';
import { CustomRouteReuseStrategy } from './common/custom-route-reuse-strategy';
import { MachineIdInterceptor } from './common/interceptors/machine-id.interceptor';
import { OrientationWarningComponent } from './orientation-warning/orientation-warning.component';
import { LottieModule } from 'ngx-lottie';
import { AppIdInterceptor } from './common/interceptors/app-id.interceptor';
import { UnsupportedDeviceComponent } from './unsupported-device/unsupported-device.component';
import { UserMachineIdService } from './common/user-machine-id.service';

// https://www.npmjs.com/package/ngx-lottie/v/9.1.0
// We use lazy loading for Lottie as it's a big bundle size and it makes sense to load only if there are any lottie animations
export function playerFactory() {
  return import(
    // https://www.npmjs.com/package/ngx-lottie/v/9.1.0#reducing-lottie-web-bundle-size
    /* webpackChunkName: 'lottie-web' */ 'lottie-web/build/player/lottie_svg'
  );
}

export function initializeApp(
  settingsService: SettingsService,
  userMachineIdService: UserMachineIdService
) {
  return (): Promise<void> => {
    if (typeof window !== 'undefined') {
      settingsService.initFromQueryParams();
      userMachineIdService.checkForMachineIdOrRedirect();
      return;
    }
  };
}

@NgModule({
  declarations: [
    AppComponent,
    CookiePopupComponent,
    WarningComponent,
    NotFoundComponent,
    UnsupportedDeviceComponent,
    OrientationWarningComponent,
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    LottieModule.forRoot({ player: playerFactory }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MachineIdInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppIdInterceptor,
      multi: true,
    },
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    {
      provide: LocationStrategy,
      useClass: PreserveQueryParamsStrategy,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({}),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [SettingsService, UserMachineIdService, Sentry.TraceService],
      multi: true,
    },
    AnalyticsService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private router: Router,
    private viewportScroller: ViewportScroller
  ) {
    // Disable automatic scroll restoration to avoid race conditions
    this.viewportScroller.setHistoryScrollRestoration('manual');

    this.handleScrollOnNavigation();
  }

  /**
   * When route is changed, Angular interprets a simple query params change as "forward navigation" too.
   * Using the pairwise function allows us to have both the previous and current router events, which we can
   * use to effectively compare the two navigation events and see if they actually change route, or only
   * the route parameters (i.e. selections stored in query params).
   *
   * Related to: https://github.com/angular/angular/issues/26744
   */
  private handleScrollOnNavigation(): void {
    this.router.events
      .pipe(
        filter((e: Event): e is Scroll => e instanceof Scroll),
        pairwise()
      )
      .subscribe((e: Scroll[]) => {
        const previous = e[0];
        const current = e[1];
        if (current.position) {
          // Backward navigation
          this.viewportScroller.scrollToPosition(current.position);
        } else if (current.anchor) {
          // Anchor navigation
          this.viewportScroller.scrollToAnchor(current.anchor);
        } else {
          // Check if routes match, or if it is only a query param change
          if (
            this.getBaseRoute(previous.routerEvent.urlAfterRedirects) !==
            this.getBaseRoute(current.routerEvent.urlAfterRedirects)
          ) {
            // Routes don't match, this is actual forward navigation
            // Default behavior: scroll to top
            // Eg: Move to the very bottom of the `Order Completed` page in the reordering app, navigate back and then come back to the page, the user page should scroll to the top
            this.viewportScroller.scrollToPosition([0, 0]);
          }
        }
      });
  }

  private getBaseRoute(url: string): string {
    // return url without query params
    return url.split('?')[0];
  }
}
//
